import { useCallback, useSyncExternalStore } from 'react';

import { eventService } from '../services/eventService';
import { liveSiteService } from '../services/liveSiteService';
import type { LiveSite } from '../services/liveSiteService';

/** While a deploy is queued or running the status is re-read this often. */
const UPDATING_POLL_MS = 4000;
/** Page events arrive in bursts during generation; the status is re-read at most this often for them. */
const PAGE_EVENT_REFRESH_MS = 4000;

interface LiveSiteEntry {
  inFlight: Promise<void> | null;
  lastFetchedAt: number;
  listeners: Set<() => void>;
  pollTimer: ReturnType<typeof setTimeout> | null;
  site: LiveSite | null;
  stopWatching?: () => void;
}

const entries = new Map<string, LiveSiteEntry>();

const entryFor = (projectId: string): LiveSiteEntry => {
  const existing = entries.get(projectId);

  if (existing) return existing;
  const created: LiveSiteEntry = {
    inFlight: null, lastFetchedAt: 0, listeners: new Set(), pollTimer: null, site: null,
  };

  entries.set(projectId, created);

  return created;
};

const publish = (entry: LiveSiteEntry, site: LiveSite): void => {
  entry.site = site;
  entry.listeners.forEach((listener) => listener());
};

const schedulePoll = (projectId: string, entry: LiveSiteEntry): void => {
  if (entry.pollTimer) clearTimeout(entry.pollTimer);
  entry.pollTimer = null;
  if (!entry.site?.updating || entry.listeners.size === 0) return;
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  entry.pollTimer = setTimeout(() => { void refreshLiveSite(projectId); }, UPDATING_POLL_MS);
};

/** Re-read a project's live link now (shared by every component showing it). */
export const refreshLiveSite = (projectId: string): Promise<void> => {
  const entry = entryFor(projectId);

  if (entry.inFlight) return entry.inFlight;
  entry.lastFetchedAt = Date.now();
  entry.inFlight = liveSiteService.get(projectId)
    .then((site) => publish(entry, site))
    .catch(() => { /* keep the last known link; the next event or poll tries again */ })
    .finally(() => {
      entry.inFlight = null;
      schedulePoll(projectId, entry);
    });

  return entry.inFlight;
};

const belongsTo = (projectId: string, data: unknown): boolean => {
  const payload = data as { project_id?: string | null } | null;

  return payload?.project_id ? payload.project_id === projectId : eventService.isCurrentProject(projectId);
};

const watch = (projectId: string, entry: LiveSiteEntry): (() => void) => {
  const refresh = () => { void refreshLiveSite(projectId); };
  const onPages = (data: unknown) => {
    if (belongsTo(projectId, data) && Date.now() - entry.lastFetchedAt >= PAGE_EVENT_REFRESH_MS) refresh();
  };
  const stops = [
    eventService.subscribe('live_site', (data: unknown) => { if (belongsTo(projectId, data)) refresh(); }),
    eventService.subscribe('design_pages', onPages),
    eventService.subscribe('connected', refresh),
  ];

  return () => stops.forEach((stop) => stop());
};

const subscribe = (projectId: string, listener: () => void): (() => void) => {
  const entry = entryFor(projectId);
  // Nothing watched this project's events while no one showed it: read it again on the way back.
  const resuming = !entry.stopWatching;

  entry.listeners.add(listener);
  if (resuming) entry.stopWatching = watch(projectId, entry);
  if (resuming && Date.now() - entry.lastFetchedAt >= PAGE_EVENT_REFRESH_MS) void refreshLiveSite(projectId);

  return () => {
    entry.listeners.delete(listener);
    if (entry.listeners.size > 0) return;
    entry.stopWatching?.();
    entry.stopWatching = undefined;
  };
};

/** Show a fresh status right away (e.g. the answer to a Redeploy press). */
export const setLiveSite = (projectId: string, site: LiveSite): void => {
  const entry = entryFor(projectId);

  publish(entry, site);
  schedulePoll(projectId, entry);
};

export const resetLiveSitesForTests = (): void => {
  entries.forEach((entry) => {
    entry.stopWatching?.();
    if (entry.pollTimer) clearTimeout(entry.pollTimer);
  });
  entries.clear();
};

/** A project's live link and its deploy status; null until the first status read answers. */
export const useLiveSite = (projectId: string | null | undefined): LiveSite | null => {
  const subscribeToProject = useCallback((listener: () => void) => (
    projectId ? subscribe(projectId, listener) : () => undefined
  ), [projectId]);
  const getSnapshot = useCallback(() => (projectId ? entryFor(projectId).site : null), [projectId]);

  return useSyncExternalStore(subscribeToProject, getSnapshot, () => null);
};

/** The project's live URL once it has one; what other surfaces (e.g. the page-ready message) render. */
export const useLiveSiteUrl = (projectId: string | null | undefined): string | null => (
  useLiveSite(projectId)?.url ?? null
);
